import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { AppFeature } from '../types/ViewModel/AppFeature/AppFeature';
import { AppsMenuHtml } from '../types/ViewModel/AppsMenuHtml';
import { CargoTemperatureLogDate } from '../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { NoonReportStatus } from '../types/ViewModel/Aol/NoonReportStatus';
import { Resolution } from 'src/types/ViewModel/Resolution';
import { AppModalData } from 'src/types/ModalData';

type AppContextType = {
    appsMenu: AppsMenuHtml | null;
    selectedResolution: Resolution | null;
    defaultResolution: Resolution | null;
    resolutionList: Resolution[] | null;
    visibleSidePanelModuleIndex: number;
    visibleRotationFilterPanel: boolean;
    isPrinterPanelOpen: boolean;
    isFilterPanelOpen: boolean | null;
    isVoyageSelectionPanelOpen: boolean | null;
    isSettingsPanelOpen: boolean | null;
    isReportAolFilterPanelOpen: boolean | null;
    isRotationFilterActive: boolean | null;
    serverAppFeatureSettings: AppFeature[];
    appFeatureSettings: AppFeature[];
    appRefreshPostponed: Date | null;
    missingRobStatuses: number[];
    missingNoonReportStatuses: NoonReportStatus[] | null;
    missingCargoTempLogStatuses: CargoTemperatureLogDate[] | null;
    numMissingReportAol: number | null;
    numMissingApprovedAol: number | null;
    numMissingBunkerRegistrations: number | null;
    numMissingBunkerRob: number | null;
    appModalData: AppModalData | null;
    setAppsMenu: (appsMenuHtml: AppsMenuHtml | null) => void;
    setSelectedResolution: (resolution: Resolution) => void;
    setDefaultResolution: (resolution: Resolution) => void;
    setResolutionList: (resolutions: Resolution[]) => void;
    setVisibleSidePanelModuleIndex: (isVisible: number) => void;
    setVisibleRotationFilterPanel: (isVisible: boolean) => void;
    setIsPrinterPanelOpen: (isVisible: boolean) => void;
    setIsFilterPanelOpen: (status: boolean | null) => void;
    setIsVoyageSelectionPanelOpen: (status: boolean | null) => void;
    setIsSettingsPanelOpen: (status: boolean | null) => void;
    setIsReportAolFilterPanelOpen: (status: boolean | null) => void;
    setIsRotationFilterActive: (active: boolean | null) => void;
    setServerAppFeatureSettings: (features: AppFeature[]) => void;
    setAppFeatureSettings: (features: AppFeature[]) => void;
    setAppRefreshPostponed: (postponedTo: Date) => void;
    setMissingRobStatuses: (chemicalRobPortCalls: number[]) => void;
    setMissingNoonReportStatuses: (noonReports: NoonReportStatus[] | null) => void;
    setMissingCargoTempLogStatuses: (cargoTempLogDates: CargoTemperatureLogDate[] | null) => void;
    setNumMissingReportAol: (num: number) => void;
    setNumMissingApprovedAol: (num: number) => void;
    setNumMissingBunkerRegistrations: (num: number) => void;
    setNumMissingBunkerRob: (num: number) => void;
    setAppModalData: (appModalData: AppModalData | null) => void;
};

export const AppContext = React.createContext<AppContextType>(undefined);

export const AppContextProvider = ({ children }: ContextProps) => {
    const [appsMenu, setAppsMenu] = useState<AppsMenuHtml | null>(null);
    const [selectedResolution, setSelectedResolution] = useState<Resolution | null>(new Resolution(window.innerWidth, window.innerHeight, true));
    const [defaultResolution, setDefaultResolution] = useState<Resolution | null>(new Resolution(window.innerWidth, window.innerHeight, true));
    const [resolutionList, setResolutionList] = useState<Resolution[] | null>([]);
    const [visibleSidePanelModuleIndex, setVisibleSidePanelModuleIndex] = useState(-1);
    const [visibleRotationFilterPanel, setVisibleRotationFilterPanel] = useState(false);
    const [isPrinterPanelOpen, setIsPrinterPanelOpen] = useState<boolean>(false);
    const [isRotationFilterActive, setIsRotationFilterActive] = useState<boolean | null>(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean | null>(null);
    const [isVoyageSelectionPanelOpen, setIsVoyageSelectionPanelOpen] = useState<boolean | null>(null);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState<boolean | null>(null);
    const [isReportAolFilterPanelOpen, setIsReportAolFilterPanelOpen] = useState<boolean | null>(null);
    const [serverAppFeatureSettings, setServerAppFeatureSettings] = useState<AppFeature[]>([]);
    const [appFeatureSettings, setAppFeatureSettings] = useState<AppFeature[]>([]);
    const [appRefreshPostponed, setAppRefreshPostponed] = useState<Date | null>(null);
    const [missingRobStatuses, setMissingRobStatuses] = useState<number[]>([]);
    const [missingNoonReportStatuses, setMissingNoonReportStatuses] = useState<NoonReportStatus[] | null>(null);
    const [missingCargoTempLogStatuses, setMissingCargoTempLogStatuses] = useState<CargoTemperatureLogDate[] | null>(null);
    const [numMissingReportAol, setNumMissingReportAol] = useState<number | null>(null);
    const [numMissingApprovedAol, setNumMissingApprovedAol] = useState<number | null>(null);
    const [numMissingBunkerRegistrations, setNumMissingBunkerRegistrations] = useState<number | null>(null);
    const [numMissingBunkerRob, setNumMissingBunkerRob] = useState<number | null>(null);
    const [appModalData, setAppModalData] = useState<AppModalData | null>(null);

    return (
        <AppContext.Provider
            value={{
                appsMenu,
                setAppsMenu,
                selectedResolution,
                setSelectedResolution,
                defaultResolution,
                setDefaultResolution,
                resolutionList,
                setResolutionList,
                visibleSidePanelModuleIndex,
                setVisibleSidePanelModuleIndex,
                visibleRotationFilterPanel,
                setVisibleRotationFilterPanel,
                isPrinterPanelOpen,
                setIsPrinterPanelOpen,
                isRotationFilterActive,
                setIsRotationFilterActive,
                isFilterPanelOpen,
                setIsFilterPanelOpen,
                isVoyageSelectionPanelOpen,
                setIsVoyageSelectionPanelOpen,
                isReportAolFilterPanelOpen,
                setIsReportAolFilterPanelOpen,
                isSettingsPanelOpen,
                setIsSettingsPanelOpen,
                serverAppFeatureSettings,
                setServerAppFeatureSettings,
                appFeatureSettings,
                setAppFeatureSettings,
                appRefreshPostponed,
                setAppRefreshPostponed,
                missingRobStatuses,
                setMissingRobStatuses,
                missingNoonReportStatuses,
                setMissingNoonReportStatuses,
                missingCargoTempLogStatuses,
                setMissingCargoTempLogStatuses,
                numMissingReportAol,
                setNumMissingReportAol,
                numMissingApprovedAol,
                setNumMissingApprovedAol,
                numMissingBunkerRegistrations,
                setNumMissingBunkerRegistrations,
                numMissingBunkerRob,
                setNumMissingBunkerRob,
                appModalData,
                setAppModalData,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useAppStore = () => useContext(AppContext);
